import React, {
    useEffect,
    useState
} from 'react';
import { Link } from 'react-router-dom';
import './adminDashboard.css';
import getImageUrl from '../../../utils/getImageUrl'
import API_BASE_URL from '../../../config/api';

const AdminDashboard = () => {
    const [stats, setStats] = useState({
        todayVisitors: 0,
        monthVisitors: 0,
        waitingInquiries: 0,
        processingInquiries: 0,
        noticeCount: 0,
        faqCount: 0
    });

    const [recentInquiries, setRecentInquiries] = useState([]);
    const [recentPortfolios, setRecentPortfolios] = useState([]);

    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState('');

    useEffect(() => {
        const getDashboardData = async () => {
            try {
                setIsLoading(true);
                setError('');

                const [
                    dashboardResponse,
                    inquiryResponse,
                    portfolioResponse
                ] = await Promise.all([
                    fetch(
                        `${API_BASE_URL}/api/admin/dashboard`,
                        {
                            method: 'GET',
                            credentials: 'include'
                        }
                    ),
                    fetch(
                        `${API_BASE_URL}/api/inquiries`,
                        {
                            method: 'GET',
                            credentials: 'include'
                        }
                    ),
                    fetch(
                        `${API_BASE_URL}/api/portfolios`,
                        {
                            method: 'GET',
                            credentials: 'include'
                        }
                    )
                ]);

                const dashboardData =
                    await dashboardResponse.json();

                const inquiryData =
                    await inquiryResponse.json();

                const portfolioData =
                    await portfolioResponse.json();

                if (!dashboardResponse.ok) {
                    throw new Error(
                        dashboardData.message ||
                        '대시보드 정보를 불러오지 못했습니다.'
                    );
                }

                setStats(
                    dashboardData.stats || {
                        todayVisitors: 0,
                        monthVisitors: 0,
                        waitingInquiries: 0,
                        processingInquiries: 0,
                        noticeCount: 0,
                        faqCount: 0
                    }
                );

                if (inquiryResponse.ok) {
                    const inquiries =
                        Array.isArray(inquiryData)
                            ? inquiryData
                            : inquiryData.inquiries || [];

                    const sortedInquiries =
                        [...inquiries]
                            .sort((a, b) => {
                                return (
                                    new Date(b.created_at) -
                                    new Date(a.created_at)
                                );
                            })
                            .slice(0, 5);

                    setRecentInquiries(
                        sortedInquiries
                    );
                } else {
                    console.error(
                        '최근 견적문의 조회 실패:',
                        inquiryData
                    );

                    setRecentInquiries([]);
                }

                if (portfolioResponse.ok) {
                    const portfolios =
                        Array.isArray(portfolioData)
                            ? portfolioData
                            : portfolioData.portfolios || [];

                    const sortedPortfolios =
                        [...portfolios]
                            .sort((a, b) => {
                                return (
                                    new Date(b.created_at) -
                                    new Date(a.created_at)
                                );
                            })
                            .slice(0, 5);

                    setRecentPortfolios(
                        sortedPortfolios
                    );
                } else {
                    console.error(
                        '최근 시공사례 조회 실패:',
                        portfolioData
                    );

                    setRecentPortfolios([]);
                }
            } catch (error) {
                console.error(
                    '대시보드 조회 오류:',
                    error
                );

                setError(
                    error.message ||
                    '대시보드 정보를 불러오지 못했습니다.'
                );
            } finally {
                setIsLoading(false);
            }
        };

        getDashboardData();
    }, []);

    const formatNumber = (value) => {
        return Number(
            value || 0
        ).toLocaleString();
    };

    const formatDate = (value) => {
        if (!value) {
            return '-';
        }

        const date =
            new Date(value);

        if (
            Number.isNaN(
                date.getTime()
            )
        ) {
            return value.substring(
                0,
                10
            );
        }

        return date
            .toLocaleDateString(
                'ko-KR',
                {
                    year: 'numeric',
                    month: '2-digit',
                    day: '2-digit'
                }
            )
            .replace(/\s/g, '');
    };

    const getInquiryStatus = (status) => {
        switch (status) {
            case 'WAITING':
                return {
                    label: '신규',
                    className: 'waiting'
                };

            case 'PROCESSING':
                return {
                    label: '처리중',
                    className: 'processing'
                };

            case 'COMPLETED':
                return {
                    label: '완료',
                    className: 'completed'
                };

            default:
                return {
                    label: status || '-',
                    className: ''
                };
        }
    };

    return (
        <div className="admin-dashboard">
            <div className="admin-page-heading">
                <div>
                    <p>
                        DASHBOARD
                    </p>

                    <h1>
                        대시보드
                    </h1>

                    <span>
                        MTMEDIA 홈페이지 운영 현황을 확인하세요.
                    </span>
                </div>
            </div>

            {error && (
                <div className="admin-dashboard-error">
                    <i className="mdi mdi-alert-circle-outline"></i>

                    <span>
                        {error}
                    </span>
                </div>
            )}

            {/* =============================================
                STAT
            ============================================= */}
            <div className="admin-stat-grid">
                <div className="admin-stat-card">
                    <div className="admin-stat-icon">
                        <i className="mdi mdi-account-clock-outline"></i>
                    </div>

                    <div className="admin-stat-info">
                        <span>
                            오늘 방문자
                        </span>

                        <strong>
                            {isLoading
                                ? '-'
                                : formatNumber(
                                    stats.todayVisitors
                                )
                            }
                        </strong>

                        <p>
                            오늘 방문한 사용자
                        </p>
                    </div>
                </div>

                <div className="admin-stat-card">
                    <div className="admin-stat-icon">
                        <i className="mdi mdi-chart-line"></i>
                    </div>

                    <div className="admin-stat-info">
                        <span>
                            이번 달 방문자
                        </span>

                        <strong>
                            {isLoading
                                ? '-'
                                : formatNumber(
                                    stats.monthVisitors
                                )
                            }
                        </strong>

                        <p>
                            이번 달 방문한 사용자
                        </p>
                    </div>
                </div>

                <div className="admin-stat-card admin-stat-card-waiting">
                    <div className="admin-stat-icon admin-stat-icon-red">
                        <i className="mdi mdi-email-alert-outline"></i>
                    </div>

                    <div className="admin-stat-info">
                        <span>
                            신규 견적문의
                        </span>

                        <strong className="admin-stat-number-red">
                            {isLoading
                                ? '-'
                                : formatNumber(
                                    stats.waitingInquiries
                                )
                            }
                        </strong>

                        <p>
                            확인이 필요한 문의
                        </p>
                    </div>
                </div>

                <div className="admin-stat-card admin-stat-card-processing">
                    <div className="admin-stat-icon admin-stat-icon-blue">
                        <i className="mdi mdi-progress-clock"></i>
                    </div>

                    <div className="admin-stat-info">
                        <span>
                            처리중 문의
                        </span>

                        <strong className="admin-stat-number-blue">
                            {isLoading
                                ? '-'
                                : formatNumber(
                                    stats.processingInquiries
                                )
                            }
                        </strong>

                        <p>
                            현재 상담 진행 중
                        </p>
                    </div>
                </div>

                <div className="admin-stat-card">
                    <div className="admin-stat-icon">
                        <i className="mdi mdi-bullhorn-outline"></i>
                    </div>

                    <div className="admin-stat-info">
                        <span>
                            공지사항
                        </span>

                        <strong>
                            {isLoading
                                ? '-'
                                : formatNumber(
                                    stats.noticeCount
                                )
                            }
                        </strong>

                        <p>
                            등록된 공지사항
                        </p>
                    </div>
                </div>

                <div className="admin-stat-card">
                    <div className="admin-stat-icon">
                        <i className="mdi mdi-help-circle-outline"></i>
                    </div>

                    <div className="admin-stat-info">
                        <span>
                            FAQ
                        </span>

                        <strong>
                            {isLoading
                                ? '-'
                                : formatNumber(
                                    stats.faqCount
                                )
                            }
                        </strong>

                        <p>
                            현재 공개된 FAQ
                        </p>
                    </div>
                </div>
            </div>

            {/* =============================================
                RECENT DATA
            ============================================= */}
            <div className="admin-dashboard-grid">
                {/* =============================================
                    RECENT INQUIRY
                ============================================= */}
                <section className="admin-dashboard-section">
                    <div className="admin-dashboard-section-heading">
                        <div>
                            <h2>
                                최근 견적문의
                            </h2>

                            <p>
                                최근 접수된 고객 문의를 확인할 수 있습니다.
                            </p>
                        </div>

                        <Link
                            to="/admin/inquiries"
                            className="admin-dashboard-more"
                        >
                            전체보기

                            <i className="mdi mdi-chevron-right"></i>
                        </Link>
                    </div>

                    {isLoading ? (
                        <div className="admin-dashboard-empty">
                            <div className="admin-dashboard-empty-icon">
                                <i className="mdi mdi-loading mdi-spin"></i>
                            </div>

                            <strong>
                                불러오는 중입니다.
                            </strong>
                        </div>
                    ) : recentInquiries.length > 0 ? (
                        <div className="admin-dashboard-recent-list">
                            {recentInquiries.map((item) => {
                                const status =
                                    getInquiryStatus(
                                        item.status
                                    );

                                return (
                                    <Link
                                        to="/admin/inquiries"
                                        className="admin-dashboard-inquiry-item"
                                        key={item.inquiry_id}
                                    >
                                        <div className="admin-dashboard-inquiry-main">
                                            <div className="admin-dashboard-inquiry-top">
                                                <strong>
                                                    {item.name || '-'}
                                                </strong>

                                                <span
                                                    className={`admin-dashboard-status ${status.className}`}
                                                >
                                                    {status.label}
                                                </span>
                                            </div>

                                            <p>
                                                {item.equipment || '장비 미선택'}
                                                {item.location
                                                    ? ` · ${item.location}`
                                                    : ''
                                                }
                                            </p>
                                        </div>

                                        <div className="admin-dashboard-inquiry-meta">
                                            <span>
                                                {item.phone || '-'}
                                            </span>

                                            <small>
                                                {formatDate(
                                                    item.created_at
                                                )}
                                            </small>
                                        </div>

                                        <i className="mdi mdi-chevron-right admin-dashboard-item-arrow"></i>
                                    </Link>
                                );
                            })}
                        </div>
                    ) : (
                        <div className="admin-dashboard-empty">
                            <div className="admin-dashboard-empty-icon">
                                <i className="mdi mdi-email-outline"></i>
                            </div>

                            <strong>
                                최근 견적문의가 없습니다.
                            </strong>

                            <p>
                                새로운 견적문의가 접수되면
                                <br />
                                이곳에 표시됩니다.
                            </p>
                        </div>
                    )}
                </section>

                {/* =============================================
                    RECENT PORTFOLIO
                ============================================= */}
                <section className="admin-dashboard-section">
                    <div className="admin-dashboard-section-heading">
                        <div>
                            <h2>
                                최근 시공사례
                            </h2>

                            <p>
                                최근 등록한 시공사례를 확인할 수 있습니다.
                            </p>
                        </div>

                        <Link
                            to="/admin/portfolios"
                            className="admin-dashboard-more"
                        >
                            전체보기

                            <i className="mdi mdi-chevron-right"></i>
                        </Link>
                    </div>

                    {isLoading ? (
                        <div className="admin-dashboard-empty">
                            <div className="admin-dashboard-empty-icon">
                                <i className="mdi mdi-loading mdi-spin"></i>
                            </div>

                            <strong>
                                불러오는 중입니다.
                            </strong>
                        </div>
                    ) : recentPortfolios.length > 0 ? (
                        <div className="admin-dashboard-recent-list">
                            {recentPortfolios.map((item) => (
                                <Link
                                    to={`/admin/portfolios/${item.portfolio_id}/edit`}
                                    className="admin-dashboard-portfolio-item"
                                    key={item.portfolio_id}
                                >
                                    <div className="admin-dashboard-portfolio-thumbnail">
                                        {item.thumbnail_image ? (
                                            <img
                                                src={getImageUrl(item.thumbnail_image)}
                                                alt={item.title || '시공사례'}
                                            />
                                        ) : (
                                            <div className="admin-dashboard-no-image">
                                                <i className="mdi mdi-image-outline"></i>
                                            </div>
                                        )}
                                    </div>

                                    <div className="admin-dashboard-portfolio-info">
                                        <span>
                                            {item.category || '시공사례'}
                                        </span>

                                        <strong>
                                            {item.title || '-'}
                                        </strong>

                                        <p>
                                            {item.location || '지역 정보 없음'}
                                        </p>
                                    </div>

                                    <div className="admin-dashboard-portfolio-meta">
                                        <span
                                            className={
                                                item.is_active
                                                    ? 'active'
                                                    : 'inactive'
                                            }
                                        >
                                            {item.is_active
                                                ? '공개'
                                                : '비공개'
                                            }
                                        </span>

                                        <small>
                                            {formatDate(
                                                item.created_at
                                            )}
                                        </small>
                                    </div>

                                    <i className="mdi mdi-chevron-right admin-dashboard-item-arrow"></i>
                                </Link>
                            ))}
                        </div>
                    ) : (
                        <div className="admin-dashboard-empty">
                            <div className="admin-dashboard-empty-icon">
                                <i className="mdi mdi-image-multiple-outline"></i>
                            </div>

                            <strong>
                                등록된 시공사례가 없습니다.
                            </strong>

                            <p>
                                새로운 시공사례를 등록하면
                                <br />
                                이곳에 표시됩니다.
                            </p>
                        </div>
                    )}
                </section>
            </div>
        </div>
    );
};

export default AdminDashboard;